import { Button, Chip, Typography } from "@platform/design-system";
import { Avatar, Box, Stack } from "@mui/material";

import { AttachmentRow } from "./AttachmentRow";

import type { MessageSummary } from "../../messageSummary";

export interface MessageCardProps {
  message: MessageSummary;
  /**
   * Toggles this message's read state. Absent on a surface that cannot write,
   * which hides the control rather than showing one that is refused.
   */
  onSetRead?: (messageId: string, read: boolean) => void;
}

/**
 * One message in the conversation.
 *
 * The body renders as text with its line breaks kept, never as HTML. Inbound
 * mail is stored exactly as the carrier sent it, so the stored markup is a
 * stranger's and belongs nowhere near the page; `textOf` strips it to words
 * for the same reason.
 */
export const MessageCard = ({
  message,
  onSetRead,
}: MessageCardProps): React.JSX.Element => (
  <Stack
    data-testid="message-card"
    data-message-id={message.id}
    spacing={1}
    sx={{
      border: 1,
      borderColor: "divider",
      borderRadius: 1,
      p: 2,
      backgroundColor: "background.paper",
    }}
  >
    <Stack direction="row" alignItems="center" spacing={1.5}>
      <Avatar sx={{ width: 32, height: 32, fontSize: 14 }}>
        {message.initials}
      </Avatar>

      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography data-testid="message-card-author" variant="header-small">
            {message.author}
          </Typography>

          {message.byRumi && (
            <Chip
              component="span"
              size="small"
              color="info"
              variant="subtle"
              leadingVisual={false}
              label="RUMI"
            />
          )}

          {message.isDraft && (
            <Chip
              component="span"
              size="small"
              color="attention"
              variant="subtle"
              leadingVisual={false}
              label="Unsent draft"
            />
          )}

          {!message.read && (
            <Box
              data-testid="message-card-unread-dot"
              aria-label="Unread"
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: "brandPrimary.main",
                flexShrink: 0,
              }}
            />
          )}
        </Stack>

        <Typography
          data-testid="message-card-time"
          variant="caption"
          color="textSecondary"
        >
          {message.at}
        </Typography>
      </Box>

      {onSetRead && (
        <Button
          data-testid="message-card-read-toggle"
          // Read is the quieter state to move to, so marking read is offered
          // as a button and marking unread as text.
          variant={message.read ? "tertiary" : "secondary"}
          size="sm"
          onClick={() => onSetRead(message.id, !message.read)}
        >
          {message.read ? "Mark as unread" : "Mark as read"}
        </Button>
      )}
    </Stack>

    <Typography
      data-testid="message-card-body"
      variant="body-medium"
      // The stored text carries the sender's own line breaks, and collapsing
      // them runs a quoted reply into one paragraph.
      sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
    >
      {message.body}
    </Typography>

    {message.bodyFromHtml && (
      <Typography
        data-testid="message-card-from-html"
        variant="caption"
        color="textSecondary"
      >
        This message was sent as HTML. Its wording is shown without the
        sender&apos;s layout, so tables and lists may read out of shape.
      </Typography>
    )}

    {message.attachments.length > 0 && (
      <Stack spacing={1}>
        {message.attachments.map((attachment) => (
          <AttachmentRow key={attachment.id} attachment={attachment} />
        ))}
      </Stack>
    )}
  </Stack>
);
